import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { rupiah } from '../lib/format';
import { Warning, TrendUp, Wallet, Coins } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { TopProducts } from '../components/dashboard/TopProducts';
import { ShareDonut } from '../components/dashboard/ShareDonut';
import { ProductRanking } from '../components/dashboard/ProductRanking';
import { CostBreakdown } from '../components/dashboard/CostBreakdown';
import { AiInsightCard } from '../components/ai/AiInsightCard';

const PERIODS = [['bulan_ini', 'Bulan Ini'], ['30hari', '30 Hari'], ['semua', 'Semua Waktu']];

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState('bulan_ini');
  const [breakdown, setBreakdown] = useState(null);
  const [costs, setCosts] = useState([]);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get('/summary'), api.get('/report/cost-breakdown')]);
      setSummary(s.data);
      setCosts(c.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get('/report/breakdown', { params: { period } }).then((r) => setBreakdown(r.data)).catch(() => {});
  }, [period, summary]);

  const seedSample = async () => {
    setSeeding(true);
    try {
      await api.post('/seed');
      toast.success('Contoh data berhasil dimuat');
      load();
    } catch (e) {
      toast.error('Gagal memuat contoh data');
    } finally {
      setSeeding(false);
    }
  };

  const removeSample = async () => {
    try {
      await api.delete('/samples');
      toast.success('Data contoh dihapus');
      load();
    } catch (e) {
      toast.error('Gagal menghapus data contoh');
    }
  };

  const stats = [
    { label: 'Omzet Bulan Ini', value: rupiah(summary?.omzet_bulan_ini), icon: TrendUp, tone: 'text-emerald-600' },
    { label: 'Laba Bersih Bulan Ini', value: rupiah(summary?.laba_bulan_ini), icon: Coins, tone: 'text-blue-600' },
    { label: 'Total Nilai Stok', value: rupiah(summary?.nilai_stok_total), icon: Wallet, tone: 'text-indigo-600' },
    { label: 'Perlu Restok', value: `${summary?.perlu_restok || 0} item`, icon: Warning, tone: 'text-amber-600' },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={seedSample}
          disabled={seeding}
          data-testid="btn-seed-sample"
          className="px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          Muat Contoh Data (2 produk · 5 bahan)
        </button>
        <button
          onClick={removeSample}
          data-testid="btn-remove-sample"
          className="px-4 py-2.5 rounded-lg border border-border hover:bg-secondary font-medium transition-colors"
        >
          Hapus Data Contoh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="stat-card" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm text-muted-foreground">{s.label}</div>
                <div className="mt-2 font-display text-2xl font-semibold tracking-tight">{s.value}</div>
              </div>
              <s.icon size={28} weight="duotone" className={s.tone} />
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <div>
          <h2 className="font-display text-xl font-semibold">Pembagian & Segmentasi Penjualan</h2>
          <p className="text-sm text-muted-foreground">Produk terlaris, kontribusi laba, dan tempat jual</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`dash-period-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${period === k ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-secondary'}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <TopProducts top={breakdown?.top} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <ShareDonut title="Pembagian Omzet per Produk" subtitle="Porsi tiap produk dari total omzet" rows={breakdown?.products || []} valueKey="omzet" shareKey="share_omzet" testid="donut-omzet-produk" />
        <ShareDonut title="Kontribusi Laba per Produk" subtitle="Porsi tiap produk dari total laba bersih" rows={breakdown?.products || []} valueKey="laba_total" shareKey="share_laba" testid="donut-laba-produk" />
        <ShareDonut title="Omzet per Tempat Jual" subtitle="Offline, Shopee, Tokopedia, dll" rows={breakdown?.channels || []} valueKey="omzet" shareKey="share_omzet" testid="donut-saluran" />
      </div>

      <ProductRanking rows={breakdown?.products || []} />

      <AiInsightCard period={period} periodLabel={PERIODS.find(([k]) => k === period)[1]} />

      <CostBreakdown items={costs} />
    </div>
  );
}
